import React, { useState, useEffect, useMemo } from 'react';
import { User, ChevronDown } from 'lucide-react';
import { RevenueShareTerms } from './RevenueShareTerms';
import { trackEvent } from '../../utils/telemetry';
import { NUCLEAR_ENABLED } from '../../featureFlags';

// D11 (Feb 28, 2026) — Client-side accuracy score formula. MUST mirror
// `calculate_accuracy_score()` in backend/server.py exactly. We duplicate
// it here so the score can update LIVE as the user toggles, without
// waiting for a server roundtrip. The server still authoritatively
// recomputes on Save to ensure no drift.
const computeAccuracyScore = (controls) => {
  let score = 86; // base from AI/algorithm
  if (controls.username === 'on') score += 6;
  if (controls.location === 'on') score += 4;
  if (controls.social === 'on') score += 2;
  if (controls.media === 'on') score += 2;
  return Math.min(score, 100);
};

export const ControlCentre = ({
  show,
  onClose,
  onLogout,
  view,
  setView,
  // Accuracy Controls state
  accuracyMode,
  setAccuracyMode,
  accuracyScore,
  dataControls,
  setDataControls,
  saveUserPreferences,
  deleteConfirm,
  setDeleteConfirm,
  handleDeleteClick,
  confirmDelete,
  dataStats,
  dataStatsExpanded,
  setDataStatsExpanded,
  lastDeletedCount,
  // Nuclear
  executeGoNuclear,
  scrubReceipt = null,
  backendBaseUrl = '',
  isMobile = false,
  // Tooltip visibility
  hiddenTooltips = {},
  onHideTooltip,
  onRestoreTooltips,
  displayUsername = '',
}) => {
  const [settingsSaved, setSettingsSaved] = useState(false);
  // D6 regression fix (2026-02-13): drop the hardcoded "Maverick"
  // fallback which was flashing during the pre-verify render window on
  // slow networks. Now falls back to an empty string, keeping the
  // greeting semantically valid ("This is your Control Centre") until
  // verify resolves.
  const greetName = displayUsername || '';
  // D11 — Baseline snapshot of dataControls + accuracyMode at the moment
  // the Accuracy Controls view was entered. Used to REVERT unsaved
  // changes when the user closes the panel without clicking Save.
  // Captured once per panel-open (see useEffect below).
  const [baselineControls, setBaselineControls] = useState(null);
  const [baselineMode, setBaselineMode] = useState(null);

  // Capture baseline whenever Accuracy view becomes visible (and reset
  // settingsSaved). Re-runs only on (show, view) transitions, so toggles
  // mid-session don't clobber the baseline.
  useEffect(() => {
    if (show && view === 'accuracy') {
      setBaselineControls({ ...dataControls });
      setBaselineMode(accuracyMode);
      setSettingsSaved(false);
    }
  }, [show, view]);

  // Live accuracy score that updates the moment a toggle flips, before
  // any Save. Falls back to the prop value if dataControls is missing.
  const liveAccuracyScore = useMemo(
    () => (dataControls ? computeAccuracyScore(dataControls) : accuracyScore),
    [dataControls, accuracyScore]
  );

  // Wrap onClose so a user who closes WITHOUT saving has their toggles
  // reverted to the captured baseline. Matches the user-stated UX:
  // "real time updates; Save locks in; exit without save = revert".
  const handleClose = () => {
    if (!settingsSaved && baselineControls) {
      const changed =
        JSON.stringify(dataControls) !== JSON.stringify(baselineControls) ||
        accuracyMode !== baselineMode;
      if (changed) {
        setDataControls(baselineControls);
        if (typeof setAccuracyMode === 'function' && baselineMode !== null) {
          setAccuracyMode(baselineMode);
        }
      }
    }
    if (typeof onClose === 'function') onClose();
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="control-centre-overlay">
      <div className="absolute inset-0 bg-black/40" onClick={handleClose} />
      
      {view === 'main' ? (
        /* Slide 1: Control Centre Main — wider panel, content-driven
           height. D7 fix (2026-02-14):
             · Width: max-w-5xl → max-w-7xl (1024 → 1280 px) so the
               panel reads as a landscape rectangle, not a box.
             · Height: previous `min-h-[72vh]` removed — the container
               now takes its natural height from the content (greeting
               + tile grid). Operator flagged the min-height as making
               the panel "too long".
             · Tile grid: `mt-20` → `mt-10` so the modules sit closer
               to the greeting row instead of drifting into empty
               vertical space. Tile padding widened (px-3 → px-6) so
               the two-word labels have breathing room inside the
               wider tile. */
        <div
          className={`relative z-10 w-full ${isMobile ? 'max-w-sm mx-3 px-5 py-6 min-h-[60vh]' : 'max-w-7xl mx-4 px-12 py-10'} rounded-2xl flex flex-col`}
          style={{
            backgroundColor: 'rgba(8, 12, 24, 0.98)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(0, 200, 255, 0.15)',
          }}
          data-testid="control-centre-panel"
        >
          <button onClick={handleClose} className="absolute top-5 right-6 text-gray-300 hover:text-white transition-colors text-2xl" data-testid="control-centre-close">&times;</button>
          
          <div className="flex items-center gap-4 mb-2">
            <div className={`${isMobile ? 'w-10 h-10' : 'w-16 h-16'} rounded-full bg-gray-500/60 border-2 border-gray-400 flex items-center justify-center flex-shrink-0`}>
              <User className={isMobile ? "h-6 w-6 text-gray-200" : "h-9 w-9 text-gray-200"} />
            </div>
            <div>
              <h2 className={`${isMobile ? 'text-lg' : 'text-4xl'} font-semibold text-white tracking-tight`} data-testid="control-centre-greeting">{isMobile ? 'Control Centre' : (greetName ? `${greetName}, this is your Control Centre` : 'This is your Control Centre')}</h2>
            </div>
          </div>
          
          {/* Module grid — 4 compact tiles. Lowered ~3cm (mt-20 vs prior
              mt-8) so they sit visually centred, not stuck to the
              header. Each tile is 50% taller (py-14) with the label
              flex-centred both axes. All four labels use an explicit
              <br /> break so the two-word tile names split
              consistently — this fixes the D6-4 regression where
              `whitespace-nowrap` + `text-2xl` pushed "Accuracy
              Controls" / "Subscription Controls" outside the tile
              container on the previous deploy. */}
          <div className={`grid ${isMobile ? 'grid-cols-2 gap-4 mt-20' : 'grid-cols-4 gap-5 mt-10'}`}>
            {[
              {
                name: 'Session History',
                id: 'session',
                display: (<>Session<br />History</>),
              },
              {
                name: 'Accuracy Controls',
                id: 'accuracy',
                display: (<>Accuracy<br />Controls</>),
              },
              {
                name: 'Revenue Share Tracker',
                id: 'revenue',
                display: (<>Revenue Share<br />Tracker</>),
              },
              {
                name: 'Subscription Controls',
                id: 'subscription',
                display: (<>Subscription<br />Controls</>),
              },
            ].map((mod) => (
              <button
                key={mod.id}
                onClick={() => {
                  if (mod.id === 'revenue') {
                    trackEvent('view_revenue_share', { source: 'control_centre' });
                  }
                  setView(mod.id);
                }}
                className={`rounded-lg ${isMobile ? 'px-3 py-7 text-base' : 'px-6 py-14 text-xl'} text-center text-white font-semibold tracking-tight leading-tight transition-all duration-200 hover:bg-gray-400/50 cursor-pointer hover:scale-[1.03] flex items-center justify-center`}
                style={{ backgroundColor: 'rgba(160, 165, 175, 0.45)' }}
                data-testid={`cc-module-${mod.id}`}
              >
                {mod.display || mod.name}
              </button>
            ))}
          </div>
        </div>
      ) : view === 'revenue' ? (
        /* Revenue Share Tracker — reuses the same screen as the Menu's Revenue Share Terms */
        <RevenueShareTerms onBack={() => setView('main')} isMobile={isMobile} />
      ) : view === 'session' || view === 'subscription' ? (
        /* Placeholder for not-yet-built modules */
        <div
          className={`relative z-10 w-full ${isMobile ? 'max-w-sm mx-3 px-5 py-10' : 'max-w-2xl mx-4 px-10 py-14'} rounded-2xl text-center`}
          style={{
            backgroundColor: 'rgba(8, 12, 24, 0.98)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(0, 200, 255, 0.15)',
          }}
          data-testid={`cc-${view}-placeholder`}
        >
          <button
            onClick={() => setView('main')}
            className="absolute top-5 right-6 text-gray-300 hover:text-white transition-colors text-xl"
            data-testid={`cc-${view}-back-btn`}
          >
            &#x21A9;
          </button>
          <h2
            className={`${isMobile ? 'text-lg' : 'text-2xl'} font-semibold text-white tracking-tight mb-4`}
          >
            {view === 'session' ? 'Session History' : 'Subscription Controls'}
          </h2>
          <p className={`${isMobile ? 'text-sm' : 'text-base'} text-gray-200`}>
            This section currently not available.
          </p>
        </div>
      ) : view === 'accuracy' ? (
        /* Slide 2: Accuracy Controls */
        <div className={`relative z-10 w-full ${isMobile ? 'mx-2 max-h-[95vh] overflow-y-auto' : 'max-w-7xl mx-4'} rounded-2xl ${isMobile ? 'px-4 py-6' : 'px-10 py-8'}`} style={{ backgroundColor: 'rgba(8, 12, 24, 0.98)', backdropFilter: 'blur(20px)', border: '1px solid rgba(0, 200, 255, 0.15)' }} data-testid="accuracy-controls-panel">
          <button onClick={() => {
            // D11 (Feb 28, 2026) — Back button from Accuracy view also
            // reverts unsaved toggles to baseline, matching the user-
            // stated UX: "exit without save = revert to previous".
            if (!settingsSaved && baselineControls) {
              const changed =
                JSON.stringify(dataControls) !== JSON.stringify(baselineControls) ||
                accuracyMode !== baselineMode;
              if (changed) {
                setDataControls(baselineControls);
                if (typeof setAccuracyMode === 'function' && baselineMode !== null) {
                  setAccuracyMode(baselineMode);
                }
              }
            }
            setView('main');
          }} className="absolute top-5 right-6 text-gray-300 hover:text-white transition-colors text-xl" data-testid="accuracy-back-btn">&#x21A9;</button>
          
          <div className="flex items-center gap-4 mb-2">
            <div className={`${isMobile ? 'w-10 h-10' : 'w-14 h-14'} rounded-full bg-gray-500/60 border-2 border-gray-400 flex items-center justify-center flex-shrink-0`}>
              <User className={isMobile ? "h-6 w-6 text-gray-200" : "h-8 w-8 text-gray-200"} />
            </div>
            <h2 className={`${isMobile ? 'text-lg' : 'text-3xl'} font-semibold text-white tracking-tight`} data-testid="accuracy-greeting">{isMobile ? 'Accuracy Controls' : `${greetName}, these are your Accuracy Controls`}</h2>
          </div>
          
          <hr className="border-gray-500/40 my-5" />
          
          <div className={`${isMobile ? 'flex flex-col gap-6' : 'flex gap-10'}`}>
            {/* Left: Control Settings */}
            <div className="flex-shrink-0" style={isMobile ? {} : { minWidth: '220px' }}>
              <div className="mb-8">
                <button
                  onClick={async () => {
                    await saveUserPreferences(dataControls, accuracyMode);
                    setSettingsSaved(true);
                    // D11 — commit baseline so a subsequent close-without-
                    // another-change does NOT revert this saved state.
                    setBaselineControls({ ...dataControls });
                    setBaselineMode(accuracyMode);
                  }}
                  className={`px-6 py-3 rounded-lg text-sm font-bold uppercase tracking-wide transition-all ${
                    settingsSaved
                      ? 'bg-cyan-600 text-white'
                      : 'bg-amber-500 hover:bg-amber-400 text-gray-900 hover:scale-[1.02]'
                  }`}
                  data-testid="save-accuracy-settings-btn"
                >
                  {settingsSaved ? 'Settings Saved' : 'Save Settings'}
                </button>
              </div>
              
              <div className="mb-8 relative group cursor-help">
                <p className={`${isMobile ? 'text-4xl' : 'text-6xl'} font-bold text-white tracking-tight`} data-testid="accuracy-score-value">{liveAccuracyScore}%</p>
                <p className="text-sm text-gray-300 mt-1">Accuracy Score</p>
                {!isMobile && !hiddenTooltips['accuracy_score'] && (
                  <div className="absolute left-0 top-full mt-2 p-5 bg-gray-800 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 group-hover:pointer-events-auto translate-y-1 group-hover:translate-y-0 transition-all duration-200 pointer-events-none z-50" style={{ width: '650px' }}>
                    <p className="text-sm text-white font-bold mb-3">Accuracy Score</p>
                    <p className="text-sm text-gray-200 leading-relaxed mb-3">The most fundamental aspect of search engine accuracy is understanding what you are looking for.</p>
                    <p className="text-sm text-gray-200 leading-relaxed mb-3">How you write your query and how our proprietary AI agent understands and applies your intent is the biggest factor in accuracy.</p>
                    <p className="text-sm text-gray-200 leading-relaxed mb-3">Your accuracy score is comprised of many things you control.</p>
                    <p className="text-sm text-gray-300 leading-relaxed italic">Interestingly, your "search history" only contributes a small amount to overall accuracy. But some people (nudge, nudge, wink, wink) make billions by selling your information.</p>
                    <p className="text-sm text-gray-200 leading-relaxed mt-3">If you do allow us to capture data, we may only track a select portion of the list.</p>
                    <button onClick={(e) => { e.stopPropagation(); onHideTooltip && onHideTooltip('accuracy_score'); }} className="mt-3 text-[10px] uppercase tracking-wider text-gray-500 hover:text-gray-300 transition-colors" data-testid="hide-tooltip-accuracy-score">Don't Show</button>
                  </div>
                )}
              </div>
            </div>
            
            {/* Right: Data Types + Controls */}
            <div className="flex-1 relative" style={isMobile ? {} : { maxWidth: '520px' }}>
              <div className="grid items-center mb-5" style={{ gridTemplateColumns: isMobile ? '1fr auto' : '200px 1fr' }}>
                <h3 className="text-sm font-bold text-white uppercase tracking-widest">Data Type</h3>
                <h3 className="text-sm font-bold text-white uppercase tracking-widest">Your Controls</h3>
              </div>
              {[
                { key: 'username', label: 'Username Data' },
                { key: 'location', label: 'Location' },
                { key: 'social', label: 'Social Media Activity' },
                { key: 'media', label: 'Media Rich Search' },
              ].map((item) => (
                <div key={item.key} className="grid items-center mb-6" style={{ gridTemplateColumns: isMobile ? '1fr auto' : '200px 1fr' }}>
                  <div className="relative group">
                    <p className={`text-base text-gray-200 ${item.key === 'username' || item.key === 'location' || item.key === 'social' || item.key === 'media' ? 'cursor-help' : ''}`} data-testid={`data-type-${item.key}`}>
                      {item.label}
                    </p>
                    {item.key === 'username' && !isMobile && !hiddenTooltips['username'] && (
                      <div className="absolute left-0 top-full mt-2 p-4 bg-gray-800 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 group-hover:pointer-events-auto translate-y-1 group-hover:translate-y-0 transition-all duration-200 pointer-events-none z-50" style={{ width: '850px' }}>
                        <p className="text-sm text-white font-bold mb-2">Username Data: Adds ~6% to your Accuracy Score</p>
                        <p className="text-sm text-gray-200 leading-relaxed mb-2">Your accuracy is comprised of several things you are control of.</p>
                        <p className="text-sm text-gray-200 leading-relaxed mb-3">Interestingly your "search history" only contributes a small amount to accuracy.</p>
                        <div className="space-y-1.5 text-xs text-gray-300">
                          <p>• <span className="font-bold text-gray-100">Search History:</span> Helps refine personalized and repeated queries, though more secondary.</p>
                          <p>• <span className="font-bold text-gray-100">Search Queries:</span> The most fundamental aspect of search engine accuracy is understanding what the user is looking for.</p>
                          <p>• <span className="font-bold text-gray-100">Click-Through Rates:</span> High correlation between results clicked and relevance, helping to refine future rankings.</p>
                          <p>• <span className="font-bold text-gray-100">Time Spent on Pages (Dwell Time):</span> Indicates the usefulness and engagement level of a page.</p>
                          <p>• <span className="font-bold text-gray-100">Search Context:</span> Critical for surfacing time-sensitive results such as breaking news, sports, and events.</p>
                          <p>• <span className="font-bold text-gray-100">Bounce Rate:</span> High bounce rates signal poor content or irrelevant results.</p>
                          <p>• <span className="font-bold text-gray-100">Search Session Patterns:</span> Helps understand multi-step queries or searches with multiple facets.</p>
                          <p>• <span className="font-bold text-gray-100">Previous Search Context:</span> Helps with follow-up queries and refining ongoing search sessions.</p>
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); onHideTooltip && onHideTooltip('username'); }} className="mt-3 text-[10px] uppercase tracking-wider text-gray-500 hover:text-gray-300 transition-colors" data-testid="hide-tooltip-username">Don't Show</button>
                      </div>
                    )}
                    {item.key === 'location' && !isMobile && !hiddenTooltips['location'] && (
                      <div className="absolute left-0 top-full mt-2 p-4 bg-gray-800 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 group-hover:pointer-events-auto translate-y-1 group-hover:translate-y-0 transition-all duration-200 pointer-events-none z-50" style={{ width: '800px' }}>
                        <p className="text-sm text-white font-bold mb-2">Location Data: Adds ~4% to your Accuracy Score</p>
                        <div className="space-y-1.5 text-xs text-gray-300">
                          <p>• <span className="font-bold text-gray-100">User Location:</span> Essential for providing localized results.</p>
                          <p>• <span className="font-bold text-gray-100">Local Search Patterns:</span> Local, domestic or global geo-location patterns help with relevance and provides context.</p>
                          <p>• <span className="font-bold text-gray-100">Language Preferences:</span> Important for multilingual regions.</p>
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); onHideTooltip && onHideTooltip('location'); }} className="mt-3 text-[10px] uppercase tracking-wider text-gray-500 hover:text-gray-300 transition-colors" data-testid="hide-tooltip-location">Don't Show</button>
                      </div>
                    )}
                    {item.key === 'social' && !isMobile && !hiddenTooltips['social'] && (
                      <div className="absolute left-0 top-full mt-2 p-4 bg-gray-800 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 group-hover:pointer-events-auto translate-y-1 group-hover:translate-y-0 transition-all duration-200 pointer-events-none z-50" style={{ width: '750px' }}>
                        <p className="text-sm text-white font-bold mb-2">Social Media Data: Adds approx. 2% to your accuracy score</p>
                        <div className="space-y-1.5 text-xs text-gray-300">
                          <p>• <span className="font-bold text-gray-100">Search queries and click behaviour</span></p>
                          <p>• <span className="font-bold text-gray-100">Browsing history across sites</span></p>
                          <p>• <span className="font-bold text-gray-100">Public social media content signals</span></p>
                          <p>• <span className="font-bold text-gray-100">Inferred interests and behavioural profiles</span></p>
                          <p>• <span className="font-bold text-gray-100">Advertising interaction data</span></p>
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); onHideTooltip && onHideTooltip('social'); }} className="mt-3 text-[10px] uppercase tracking-wider text-gray-500 hover:text-gray-300 transition-colors" data-testid="hide-tooltip-social">Don't Show</button>
                      </div>
                    )}
                    {item.key === 'media' && !isMobile && !hiddenTooltips['media'] && (
                      <div className="absolute left-0 top-full mt-2 p-4 bg-gray-800 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 group-hover:pointer-events-auto translate-y-1 group-hover:translate-y-0 transition-all duration-200 pointer-events-none z-50" style={{ width: '750px' }}>
                        <p className="text-sm text-white font-bold mb-2">Media Rich Search: Adds approx. 2% to your Accuracy Score</p>
                        <p className="text-xs text-gray-200 leading-relaxed mb-2">Basically anything that has images, video, audio or is an actual artefact e.g. a Document.</p>
                        <div className="space-y-1.5 text-xs text-gray-300">
                          <p>• <span className="font-bold text-gray-100">Visual search queries:</span> Images or video searched, viewed and duration</p>
                          <p>• <span className="font-bold text-gray-100">Audio &amp; Document interactions</span> or downloads</p>
                          <p>• <span className="font-bold text-gray-100">Image recognition metadata</span> and object recognition within media</p>
                          <p className="text-gray-500 italic mt-2">*Video transcription data (We don't collect this, but "others" do, weirdos)</p>
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); onHideTooltip && onHideTooltip('media'); }} className="mt-3 text-[10px] uppercase tracking-wider text-gray-500 hover:text-gray-300 transition-colors" data-testid="hide-tooltip-media">Don't Show</button>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-4" data-testid={`controls-row-${item.key}`}>
                    <button
                      onClick={() => {
                        const newControls = { ...dataControls, [item.key]: 'on' };
                        setDataControls(newControls);
                        setSettingsSaved(false);
                        if (deleteConfirm.type === item.key) {
                          setDeleteConfirm({ show: false, type: null, confirmed: false });
                        }
                        trackEvent('accuracy_control_toggle', { data_type: item.key, new_state: 'on' });
                      }}
                      className="flex items-center gap-2"
                      data-testid={`control-${item.key}-on`}
                    >
                      {/* SPD v5.76 (2026-07-12) — D1-defect-2 fix. Previously
                          the unselected dot used `border-gray-500` which was
                          too faint on the dark Accuracy Controls background
                          — the operator reported the dots as invisible even
                          though they were clickable. Each dot now has a
                          colour-coded ring matching the button's semantic
                          role (green = ON, white = OFF, red = DELETE) so
                          every state is visible without being loud. */}
                      <div className={`w-6 h-6 rounded-full border-2 transition-colors ${dataControls[item.key] === 'on' && !(deleteConfirm.show && deleteConfirm.type === item.key) ? 'bg-green-500 border-green-500' : 'bg-transparent border-green-400/60 hover:border-green-300'}`} />
                      <span className={`text-sm font-bold uppercase tracking-wide ${dataControls[item.key] === 'on' && !(deleteConfirm.show && deleteConfirm.type === item.key) ? 'text-green-400' : 'text-gray-400'}`}>on</span>
                    </button>
                    
                    <button
                      onClick={() => {
                        const newControls = { ...dataControls, [item.key]: 'off' };
                        setDataControls(newControls);
                        setSettingsSaved(false);
                        if (deleteConfirm.type === item.key) {
                          setDeleteConfirm({ show: false, type: null, confirmed: false });
                        }
                        trackEvent('accuracy_control_toggle', { data_type: item.key, new_state: 'off' });
                      }}
                      className="flex items-center gap-2"
                      data-testid={`control-${item.key}-off`}
                    >
                      <div className={`w-6 h-6 rounded-full border-2 transition-colors ${dataControls[item.key] === 'off' && !(deleteConfirm.show && deleteConfirm.type === item.key) ? 'bg-white border-white' : 'bg-transparent border-white/50 hover:border-white/80'}`} />
                      <span className={`text-sm font-bold uppercase tracking-wide ${dataControls[item.key] === 'off' && !(deleteConfirm.show && deleteConfirm.type === item.key) ? 'text-white' : 'text-gray-400'}`}>off</span>
                    </button>
                    
                    <div className="relative group">
                      <button
                        onClick={() => {
                          handleDeleteClick(item.key);
                          trackEvent('accuracy_control_toggle', { data_type: item.key, new_state: 'delete' });
                        }}
                        className="flex items-center gap-2"
                        data-testid={`control-${item.key}-delete`}
                      >
                        <div className={`w-6 h-6 rounded-full border-2 transition-colors ${deleteConfirm.show && deleteConfirm.type === item.key ? 'bg-red-500 border-red-500' : 'bg-transparent border-red-500/60 hover:border-red-400'}`} />
                        <span className={`text-sm font-bold uppercase tracking-wide ${deleteConfirm.show && deleteConfirm.type === item.key ? 'text-red-400' : 'text-gray-400 hover:text-red-400'}`}>delete</span>
                      </button>
                      {/* Delete-button hover tag — explains what Delete actually
                          does + describes the current encrypted-receipt outcome.
                          Rendered inside the .map() loop so all four delete
                          buttons (Username / Location / Social / Media) share
                          this identical copy — a single-source-of-truth for
                          the tooltip text.
                          Copy updated 2026-02-11 per D13 Pre-Deploy E2E Test
                          Defect #3 callout. Prior copy foreshadowed a
                          downloadable receipt that was "currently being worked
                          on"; new copy describes the encrypted-receipt outcome
                          that ships today. */}
                      <div
                        className="absolute right-0 bottom-full mb-2 w-[320px] p-3 bg-gray-800 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 group-hover:pointer-events-auto translate-y-1 group-hover:translate-y-0 transition-all duration-200 pointer-events-none z-50"
                        data-testid={`tooltip-delete-${item.key}`}
                      >
                        <p className="text-xs text-gray-200 leading-relaxed">
                          If you had given us approval to collect this data and then decide to click delete, that data is no longer in our system. It is completely expunged. Gonzo.
                        </p>
                        <p className="text-xs text-cyan-300 leading-relaxed mt-2">
                          Once you confirm deletion you will get an encrypted receipt of deletion that you can then audit us with if you so choose. The download is end-to-end encrypted and cannot be traced back to anyone.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Floating Confirmation Box */}
              {deleteConfirm.show && !deleteConfirm.deleted && (
                <div 
                  className="absolute w-[120px] p-2 bg-red-500 rounded shadow-xl z-50"
                  style={{ 
                    left: '440px',
                    top: deleteConfirm.type === 'username' ? '2px' : 
                         deleteConfirm.type === 'location' ? '46px' : 
                         deleteConfirm.type === 'social' ? '90px' : '134px'
                  }}
                  data-testid="delete-confirmation-box"
                >
                  <p className="text-[11px] text-white font-bold mb-0.5">Are you sure?</p>
                  <p className="text-[11px] text-white mb-1.5">
                    Delete {
                      deleteConfirm.type === 'username' ? (dataStats.totals?.username_data || 0) :
                      deleteConfirm.type === 'location' ? (dataStats.totals?.location || 0) :
                      deleteConfirm.type === 'social' ? (dataStats.totals?.social || 0) :
                      deleteConfirm.type === 'media' ? (dataStats.totals?.media || 0) : 0
                    } data points permanently
                  </p>
                  <label className="flex items-center gap-1 mb-1.5 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={deleteConfirm.confirmed}
                      onChange={(e) => setDeleteConfirm(prev => ({ ...prev, confirmed: e.target.checked }))}
                      className="w-3 h-3 rounded"
                      data-testid="delete-confirm-checkbox"
                    />
                    <span className="text-[11px] text-white">I confirm</span>
                  </label>
                  <div className="flex gap-1 w-full">
                    <button
                      onClick={confirmDelete}
                      disabled={!deleteConfirm.confirmed}
                      className={`flex-1 py-1 text-[10px] font-bold uppercase bg-gray-800 border border-gray-500 ${deleteConfirm.confirmed ? 'text-white hover:bg-gray-700' : 'text-gray-500 cursor-not-allowed'}`}
                      data-testid="delete-confirm-btn"
                    >
                      DELETE
                    </button>
                    <button
                      onClick={() => setDeleteConfirm({ show: false, type: null, confirmed: false, deleted: false })}
                      className="flex-1 py-1 text-[10px] font-bold uppercase bg-gray-800 border border-gray-500 text-white hover:bg-gray-700"
                      data-testid="delete-cancel-btn"
                    >
                      CANCEL
                    </button>
                  </div>
                </div>
              )}
              
              {/* Post-deletion confirmation */}
              {deleteConfirm.show && deleteConfirm.deleted && (
                <div 
                  className={`absolute ${deleteConfirm.pdfUrl ? 'w-[160px]' : 'w-[110px]'} p-2 bg-green-600 rounded shadow-xl z-50`}
                  style={{ 
                    left: '440px',
                    top: deleteConfirm.type === 'username' ? '20px' : 
                         deleteConfirm.type === 'location' ? '64px' : 
                         deleteConfirm.type === 'social' ? '108px' : '152px'
                  }}
                  data-testid="delete-success-box"
                >
                  <p className="text-xs text-white font-bold mb-1">✓ Deleted</p>
                  <p className="text-xs text-white mb-1.5">
                    Deleted at {deleteConfirm.timestamp}
                  </p>
                  {deleteConfirm.pdfUrl && (
                    <a
                      href={deleteConfirm.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full mb-1.5 px-1.5 py-1 text-[10px] font-bold uppercase text-center border border-white/80 bg-white/30 text-white hover:bg-white/40 rounded"
                      data-testid="delete-receipt-download-btn"
                    >
                      Download proof (PDF)
                    </a>
                  )}
                  <button
                    onClick={() => setDeleteConfirm({ show: false, type: null, confirmed: false, deleted: false })}
                    className="w-full px-1.5 py-0.5 text-[10px] font-bold uppercase border border-white/60 bg-white/20 text-white hover:bg-white/30"
                    data-testid="delete-close-btn"
                  >
                    Close
                  </button>
                </div>
              )}
              
              {/* Go Nuclear */}
              <div className={`${isMobile ? 'flex justify-center' : 'grid items-center'} mt-6`} style={isMobile ? {} : { gridTemplateColumns: '200px 1fr' }}>
                {!isMobile && <div></div>}
                <div className="flex items-center gap-3">
                  <div className="w-2 h-5"></div>
                  <span className="text-xs font-bold uppercase tracking-wide text-transparent">on</span>
                  <div className="relative group">
                    <button onClick={() => setView('nuclear')} className="flex flex-col items-center gap-1" data-testid="go-nuclear-btn">
                      <div className="w-10 h-10 rounded-full border-2 border-red-500 bg-red-500/20 flex items-center justify-center group-hover:bg-red-500/40 transition-colors">
                        <div className="w-4 h-4 rounded-full bg-red-500"></div>
                      </div>
                      <span className="text-xs font-bold uppercase tracking-wide text-white">Go Nuclear</span>
                    </button>
                    <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-3 py-1.5 bg-gray-800 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 group-hover:pointer-events-auto translate-y-1 group-hover:translate-y-0 transition-all duration-200 pointer-events-none z-50">
                      {/* Go Nuclear tooltip is permanent — no Don't Show button by design */}
                      <span className="text-xs text-red-400">(Step 1 of 2) Delete my account. Delete Everything!!!</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right: Data Stats Panel */}
            <div className={`${isMobile ? 'w-full' : 'w-48 ml-16'} relative`}>
              <div className="flex items-center gap-1 mb-5">
                <h3 className="text-sm font-bold text-white uppercase tracking-widest whitespace-nowrap"># of Data Points</h3>
                <button 
                  onClick={() => setDataStatsExpanded(!dataStatsExpanded)}
                  className="cursor-pointer hover:opacity-80 transition-opacity"
                >
                  <span className={`transform transition-transform duration-[3000ms] ${dataStatsExpanded ? 'rotate-0' : 'rotate-180'}`}>
                    <ChevronDown className="w-4 h-4 text-white" />
                  </span>
                </button>
              </div>
              <div className="overflow-hidden">
                <div 
                  className={`transition-all duration-[3000ms] ease-in-out origin-top ${dataStatsExpanded ? 'max-h-96 opacity-100 scale-y-100' : 'max-h-0 opacity-0 scale-y-0'}`}
                  style={{ transformOrigin: 'top center' }}
                >
                  <div className="bg-gray-800/50 rounded-xl px-4 pt-[7px] pb-3 border border-gray-700/50 -mt-1">
                    <div className="flex flex-col gap-[24px]">
                      <div className="flex justify-between items-center">
                        <span className="text-base text-gray-200 font-normal">Username Data</span>
                        <span className={`text-base font-bold transition-colors ${deleteConfirm.show && deleteConfirm.type === 'username' ? 'text-red-500' : 'text-cyan-400'}`}>{dataStats.totals?.username_data || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-base text-gray-200 font-normal">Location</span>
                        <span className={`text-base font-bold transition-colors ${deleteConfirm.show && deleteConfirm.type === 'location' ? 'text-red-500' : 'text-cyan-400'}`}>{dataStats.totals?.location || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-base text-gray-200 font-normal">Social Media</span>
                        <span className={`text-base font-bold transition-colors ${deleteConfirm.show && deleteConfirm.type === 'social' ? 'text-red-500' : 'text-cyan-400'}`}>{dataStats.totals?.social || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-base text-gray-200 font-normal">Media Search</span>
                        <span className={`text-base font-bold transition-colors ${deleteConfirm.show && deleteConfirm.type === 'media' ? 'text-red-500' : 'text-cyan-400'}`}>{dataStats.totals?.media || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {lastDeletedCount !== null && (
                <div className="mt-4 p-3 bg-red-900/30 rounded-lg border border-red-500/30">
                  <p className="text-xs text-red-400 font-bold uppercase mb-1">Last Deleted</p>
                  <p className="text-lg font-bold text-red-400">{lastDeletedCount} records</p>
                </div>
              )}
              
              {/* Restore hidden tooltips */}
              {Object.values(hiddenTooltips).some(Boolean) && (
                <button
                  onClick={() => onRestoreTooltips && onRestoreTooltips()}
                  className="mt-4 w-full text-[11px] uppercase tracking-wider text-gray-500 hover:text-cyan-400 transition-colors py-2 border border-gray-700/50 rounded-lg hover:border-cyan-500/30"
                  data-testid="restore-tooltips-btn"
                >
                  Restore Hidden Tooltips
                </button>
              )}
            </div>
          </div>
        </div>
      ) : view === 'nuclear' ? (
        /* Nuclear Option Screen */
        <div className={`relative z-10 w-full ${isMobile ? 'mx-2 max-h-[95vh] overflow-y-auto' : 'max-w-5xl mx-4'} rounded-2xl ${isMobile ? 'px-4 py-6' : 'px-10 py-8'}`} style={{ backgroundColor: 'rgba(8, 12, 24, 0.98)', backdropFilter: 'blur(20px)', border: '1px solid rgba(0, 200, 255, 0.15)' }} data-testid="nuclear-panel">
          <button onClick={() => setView('accuracy')} className="absolute top-5 right-6 text-gray-300 hover:text-white transition-colors text-xl" data-testid="nuclear-back-btn">&#x21A9;</button>
          
          <div className="flex items-center gap-4 mb-2">
            <div className={`${isMobile ? 'w-10 h-10' : 'w-14 h-14'} rounded-full border-2 border-gray-500 flex items-center justify-center`}>
              <User className={isMobile ? "h-6 w-6 text-gray-400" : "h-8 w-8 text-gray-400"} />
            </div>
            <h2 className={`${isMobile ? 'text-lg' : 'text-2xl'} font-bold text-white`} data-testid="nuclear-greeting">{isMobile ? 'Has it come to this?' : `${greetName}, has it come to this?`}</h2>
          </div>
          
          <div className="flex flex-col items-center justify-center mt-8">
            <p className={`${isMobile ? 'text-2xl' : 'text-4xl'} font-bold text-red-500 uppercase tracking-widest mb-8`}>The Nuclear Option</p>
            
            <ul className={`${isMobile ? 'text-sm' : 'text-xl'} text-white text-left max-w-2xl mb-4 space-y-3 list-disc list-outside pl-6`}>
              <li>The Nuclear Option deletes your Username, account and data.</li>
              <li>You will no longer be able to enter the site once you click confirm.</li>
              <li>You will also no longer be eligible for the Revenue Share.</li>
              <li>No data can be retrieved later if you change your mind as it will be completely expunged from our systems.</li>
              <li>That being said, you have "the right to be forgotten".</li>
            </ul>
            <p className={`${isMobile ? 'text-sm' : 'text-xl'} text-white text-center max-w-2xl mb-10`}>
              See our <a href="#" className="text-cyan-400 hover:text-cyan-300 underline">Privacy, Data &amp; AI Policy here</a>.
            </p>
            
            <p className="text-xl text-red-500 font-bold uppercase tracking-wider mb-4">Confirm: Go Nuclear!</p>
            
            {/* SPD v5.72 (2026-02-11) — Nuclear-execution Beta gate.
                The intro + confirm screens remain visible so testers can
                walk the flow, but the final red button is disabled and
                a tooltip explains why. Flip REACT_APP_NUCLEAR_ENABLED=true
                to re-enable at GA. */}
            <div className="relative group">
              <button
                onClick={async () => {
                  if (!NUCLEAR_ENABLED) return;
                  try {
                    await executeGoNuclear();
                    // Phase 9 — show the scrub receipt panel BEFORE the
                    // mushroom-cloud confirmation so the user gets their
                    // proof artifact in hand. The receipt view has its
                    // own "Continue" button that transitions to nuclear-confirmed.
                    setView('nuclear-receipt');
                  } catch (e) {
                    // Toast already shown by executeGoNuclear; stay on this view
                  }
                }}
                disabled={!NUCLEAR_ENABLED}
                className={`w-28 h-36 bg-white rounded-lg shadow-lg flex items-center justify-center transition-transform ${
                  NUCLEAR_ENABLED ? 'hover:scale-105 cursor-pointer' : 'opacity-50 cursor-not-allowed'
                }`}
                data-testid="nuclear-confirm-btn"
                aria-disabled={!NUCLEAR_ENABLED}
              >
                <div className={`w-16 h-16 rounded-full ${
                  NUCLEAR_ENABLED
                    ? 'bg-red-500 border-4 border-red-700 shadow-inner hover:bg-red-600'
                    : 'bg-red-500/60 border-4 border-red-700/60 shadow-inner'
                } transition-colors`}></div>
              </button>
              {!NUCLEAR_ENABLED && (
                <div
                  className="absolute left-1/2 -translate-x-1/2 bottom-full mb-3 w-[280px] p-3 bg-gray-900 border border-red-500/40 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 group-hover:pointer-events-auto -translate-y-1 group-hover:translate-y-0 transition-all duration-200 pointer-events-none z-50"
                  data-testid="nuclear-disabled-tooltip"
                >
                  {/* SPD v5.74 (2026-02-11) — Copy rewritten per operator
                      GB-003 REWRITE. Verbatim operator string:
                      "Disabled during beta. It's pretty cool though".
                      Split at the natural sentence break for header/body
                      visual hierarchy. */}
                  <p className="text-xs text-red-300 font-bold uppercase tracking-wider mb-1.5">Disabled during beta.</p>
                  <p className="text-xs text-gray-200 leading-relaxed">
                    It&apos;s pretty cool though
                  </p>
                </div>
              )}
            </div>
            
            <p className="text-sm text-gray-300 font-semibold uppercase tracking-widest mt-4">Step 2 of 2</p>
          </div>
        </div>
      ) : view === 'nuclear-receipt' ? (
        /* Phase 9 — Scrub Receipt panel. Shown immediately after the
           cascade completes, BEFORE the mushroom-cloud confirmation.
           User gets the cryptographic proof artifact in hand. */
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 overflow-y-auto"
          style={{ background: 'rgba(0,0,0,0.92)' }}
          data-testid="nuclear-receipt-panel"
        >
          <div className="relative w-full max-w-2xl my-8 bg-gray-950 border border-cyan-400/40 rounded-2xl shadow-2xl max-h-[calc(100vh-4rem)] flex flex-col">
            <div className="px-6 sm:px-10 py-5 border-b border-white/10 flex items-center gap-3 flex-shrink-0">
              <span className="text-xs uppercase tracking-[0.2em] text-cyan-300/80">
                Cryptographic Proof of Deletion · Security BRD Beta v1.2.1 §14 Phase 9
              </span>
            </div>

            <div className="px-6 sm:px-10 py-6 overflow-y-auto flex-1">
              <h2 className="text-3xl font-bold text-cyan-400 mb-3 leading-tight">
                Your account has been scrubbed.
              </h2>
              <p className="text-gray-100 text-sm leading-relaxed mb-5">
                Below is your <span className="text-cyan-300">signed scrub receipt</span> —
                a portable, server-signed record of exactly what was deleted. Search.Inc
                cannot reverse this. The receipt is the only proof we issue. Download it
                or save the deletion ID; both will let you re-fetch this page for 30 days.
              </p>

              {scrubReceipt && scrubReceipt.receipt && scrubReceipt.receipt.payload && (
                <>
                  <div className="rounded-lg border border-white/10 bg-white/5 p-4 mb-5 space-y-2 text-sm">
                    <div className="flex justify-between gap-3">
                      <span className="text-gray-400">Deletion ID</span>
                      <span className="text-white font-mono text-xs break-all" data-testid="scrub-receipt-deletion-id">
                        {scrubReceipt.deletion_id}
                      </span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span className="text-gray-400">Tenant fingerprint</span>
                      <span className="text-cyan-300 font-mono text-xs break-all">
                        {scrubReceipt.receipt.payload.tenant_id}
                      </span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span className="text-gray-400">Scrubbed at (UTC)</span>
                      <span className="text-white text-xs">
                        {scrubReceipt.receipt.payload.scrubbed_at}
                      </span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span className="text-gray-400">Cascade deletes</span>
                      <span className="text-cyan-300 font-semibold">
                        {scrubReceipt.receipt.payload.cascade_deletes}
                      </span>
                    </div>
                  </div>

                  <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-3">
                    What was scrubbed
                  </h3>
                  <div className="rounded-lg border border-white/10 bg-white/5 divide-y divide-white/5 mb-5">
                    {Object.entries(scrubReceipt.receipt.payload.rows_scrubbed_by_collection || {}).length === 0 ? (
                      <div className="px-4 py-3 text-sm text-gray-400 italic">
                        No telemetry rows were associated with this account.
                      </div>
                    ) : (
                      Object.entries(scrubReceipt.receipt.payload.rows_scrubbed_by_collection || {}).map(([coll, count]) => (
                        <div key={coll} className="flex justify-between items-baseline px-4 py-2.5 text-sm" data-testid={`scrub-row-${coll}`}>
                          <span className="text-gray-200">{coll}</span>
                          <span className="text-cyan-300 font-mono text-sm">{count} rows</span>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="rounded-lg border border-white/10 bg-white/5 p-3 mb-5">
                    <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">
                      HMAC-SHA256 signature
                    </div>
                    <div className="text-cyan-300 font-mono text-[11px] leading-relaxed break-all">
                      {scrubReceipt.receipt.signature}
                    </div>
                  </div>
                </>
              )}

              <div className="flex flex-col sm:flex-row gap-3 mb-3">
                <a
                  href={scrubReceipt && scrubReceipt.pdf_url ? scrubReceipt.pdf_url : '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-gray-950 font-semibold text-sm transition-colors"
                  data-testid="scrub-receipt-pdf-download"
                >
                  Download receipt (PDF)
                </a>
                <button
                  onClick={() => {
                    if (scrubReceipt && scrubReceipt.receipt) {
                      const json = JSON.stringify(scrubReceipt.receipt, null, 2);
                      const blob = new Blob([json], { type: 'application/json' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `search-inc-scrub-receipt-${scrubReceipt.deletion_id}.json`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }
                  }}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-white/10 hover:bg-white/15 text-white font-semibold text-sm transition-colors border border-white/20"
                  data-testid="scrub-receipt-json-download"
                >
                  Download receipt (JSON)
                </button>
              </div>

              <p className="text-[11px] text-gray-400 leading-relaxed">
                Verify independently: <span className="text-cyan-300 font-mono">hmac_sha256(server_key, canonical(payload)) == signature</span>.
                You can re-fetch this receipt as JSON for 30 days at <span className="text-cyan-300 font-mono break-all">{backendBaseUrl}/account/scrub-receipt/{scrubReceipt ? scrubReceipt.deletion_id : '…'}</span>.
              </p>
            </div>

            <div className="px-6 sm:px-10 py-4 border-t border-white/10 flex items-center justify-end bg-black/30 flex-shrink-0">
              <button
                onClick={() => setView('nuclear-confirmed')}
                className="px-5 py-2.5 rounded-lg bg-red-500 hover:bg-red-400 text-white font-semibold text-sm transition-colors"
                data-testid="scrub-receipt-continue-btn"
              >
                I have the receipt — continue
              </button>
            </div>
          </div>
        </div>
      ) : view === 'nuclear-confirmed' ? (
        /* Nuclear Confirmed - full screen, click anywhere to logout */
        <div
          className="fixed inset-0 z-[200] flex flex-col items-center justify-center cursor-pointer"
          style={{ background: 'rgba(0,0,0,0.97)' }}
          onClick={() => { onLogout(); onClose(); }}
          data-testid="nuclear-confirmed-panel"
        >
          {/* Screen flash */}
          <div className="fixed inset-0 bg-white pointer-events-none" style={{ animation: 'nuke-flash 0.6s ease forwards' }} />

          {/* Mushroom cloud */}
          <div style={{ position: 'relative', width: '200px', height: '280px', marginBottom: '3rem', flexShrink: 0 }}>

            {/* Base ring - skirt expanding at ground level */}
            <div style={{
              position: 'absolute', bottom: '0px',
              left: '50%', marginLeft: '-40px',
              width: '80px', height: '22px',
              borderRadius: '50%',
              background: 'radial-gradient(ellipse, rgba(255,140,20,0.95) 0%, rgba(200,60,0,0.65) 55%, transparent 100%)',
              animation: 'nuke-base-ring 2.4s ease-out forwards',
              animationDelay: '0.05s',
            }} />

            {/* Stem */}
            <div style={{
              position: 'absolute', bottom: '14px',
              left: '50%', marginLeft: '-11px',
              width: '22px', height: '0',
              background: 'linear-gradient(to top, rgba(255,170,40,1) 0%, rgba(220,80,10,0.85) 55%, rgba(150,45,0,0.65) 100%)',
              borderRadius: '11px 11px 0 0',
              animation: 'nuke-stem-grow 2.4s ease-out forwards',
              animationDelay: '0.1s',
              transformOrigin: 'bottom center',
              boxShadow: '0 0 18px rgba(255,130,0,0.6)',
            }} />

            {/* Fireball that rises to form cap */}
            <div style={{
              position: 'absolute', bottom: '14px',
              left: '50%', marginLeft: '-30px',
              width: '60px', height: '60px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255,255,180,1) 0%, rgba(255,170,0,0.95) 30%, rgba(230,60,0,0.85) 65%, rgba(110,20,0,0.7) 100%)',
              animation: 'nuke-fireball-rise 2.2s ease-out forwards',
              boxShadow: '0 0 50px rgba(255,180,0,0.85)',
            }} />

            {/* Cap — main mushroom head, expands from bottom-center */}
            <div style={{
              position: 'absolute', top: '18px',
              left: '50%', marginLeft: '-78px',
              width: '156px', height: '115px',
              borderRadius: '50% 50% 38% 38%',
              background: 'radial-gradient(ellipse at 50% 68%, rgba(255,210,50,0.97) 0%, rgba(255,85,0,0.92) 28%, rgba(160,30,0,0.88) 58%, rgba(45,12,4,0.85) 100%)',
              animation: 'nuke-cap-expand 3s ease-out forwards',
              animationDelay: '0.35s',
              transform: 'scale(0)',
              transformOrigin: 'bottom center',
              boxShadow: '0 0 60px rgba(255,100,0,0.55)',
            }} />

            {/* Cap outer volume ring (darker edge gives depth) */}
            <div style={{
              position: 'absolute', top: '10px',
              left: '50%', marginLeft: '-90px',
              width: '180px', height: '130px',
              borderRadius: '50% 50% 42% 42%',
              background: 'radial-gradient(ellipse at 50% 50%, transparent 38%, rgba(70,12,0,0.55) 68%, rgba(20,4,0,0.75) 100%)',
              animation: 'nuke-cap-outer 3s ease-out forwards',
              animationDelay: '0.45s',
              transform: 'scale(0)',
              transformOrigin: 'bottom center',
            }} />

          </div>

          {/* Farewell text - fades in after explosion */}
          <p className="text-xl text-white text-center max-w-xl leading-relaxed px-8" style={{ animation: 'nuke-fade-in 1.2s ease forwards', animationDelay: '2s', opacity: 0, animationFillMode: 'forwards' }}>
            Your account, username and all data related to that username has been destroyed. We bid you farewell and hope you come back as a member of the team again.
          </p>

          <p className="text-gray-500 text-sm mt-10" style={{ animation: 'nuke-fade-in 1s ease forwards', animationDelay: '3.5s', opacity: 0, animationFillMode: 'forwards' }}>
            Click anywhere to exit
          </p>
        </div>
      ) : null}
    </div>
  );
};

export default ControlCentre;
